import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { useFavorites } from "@/hooks/useFavorites";
import { getMediaById, getRelatedMedia } from "@/utils/media";
import MediaCard from "@/components/feature/MediaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import EpisodeList from "@/pages/detail/components/EpisodeList";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getMediaById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [notice, setNotice] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const related = useMemo(() => (item ? getRelatedMedia(item, 5) : []), [item]);
  const favorited = item ? isFavorite(item.id) : false;

  useSEO({
    title: item
      ? `${item.title}（${item.year}）剧情介绍与全集在线观看 - 麻豆精品`
      : "影视详情 - 麻豆精品",
    description: item
      ? `《${item.title}》是一部${item.region}${item.year}年${item.genres.join("、")}作品，由${item.director}执导，${item.actors.join("、")}主演。${item.synopsis}`
      : "麻豆精品影视详情页。",
    keywords: item ? `麻豆精品,${item.title},${item.genres.join(",")},剧情介绍` : "麻豆精品,影视详情",
  });

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const showNotice = (message: string) => {
    setNotice(message);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setNotice(null), 2600);
  };

  const handleFavorite = () => {
    if (!item) return;
    toggleFavorite(item.id);
    showNotice(favorited ? "已取消收藏" : "已加入收藏（仅保存在本机）");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      showNotice("链接已复制到剪贴板");
    } catch {
      showNotice(`复制失败，请手动复制：${url}`);
    }
  };

  const handlePlay = () => {
    showNotice("播放功能为前端演示，暂未接入真实片源");
  };

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-movie-line"
          title="未找到该影视作品"
          description="链接可能已失效，或该内容尚未收录。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const categoryLabel = item.type === "movie" ? "电影" : "电视剧";
  const categoryTo = item.type === "movie" ? "/category/movie" : "/category/tv";

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "分类" },
          { label: `${categoryLabel}分类`, to: categoryTo },
          { label: item.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] lg:gap-10">
        <div className="mx-auto w-56 sm:w-64 lg:mx-0 lg:w-full">
          <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={item.cover}
              alt={`${item.title} 海报`}
              title={`${item.title} - 麻豆精品`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                favorited
                  ? "bg-accent-500 text-foreground-950 hover:bg-accent-600"
                  : "border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-200"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true"></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              aria-label="分享"
              className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-800 transition-colors hover:bg-background-200"
            >
              <i className="ri-share-forward-line text-lg" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
              {categoryLabel}
            </span>
            {item.genres.map((genre) => (
              <span
                key={genre}
                className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
              >
                {genre}
              </span>
            ))}
          </div>

          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            {item.title}
          </h1>
          <p className="mt-2 text-sm italic text-foreground-500 md:text-base">{item.tagline}</p>

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-foreground-700">
            <span className="flex items-center gap-1.5 font-semibold text-accent-600">
              <i className="ri-star-fill" aria-hidden="true"></i>
              {item.rating.toFixed(1)} 分
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-foreground-400" aria-hidden="true"></i>
              {item.year}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line text-foreground-400" aria-hidden="true"></i>
              {item.region}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-foreground-400" aria-hidden="true"></i>
              {item.duration}
            </span>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 border-t border-background-200 pt-5 text-sm sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">导演</dt>
              <dd className="font-medium text-foreground-900">{item.director}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">上映时间</dt>
              <dd className="font-medium text-foreground-900">{item.publishedAt}</dd>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <dt className="shrink-0 text-foreground-500">主演</dt>
              <dd className="font-medium text-foreground-900">{item.actors.join(" / ")}</dd>
            </div>
          </dl>

          <div className="mt-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {item.synopsis}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handlePlay}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-fill text-lg" aria-hidden="true"></i>
              立即观看
            </button>
            <button
              type="button"
              onClick={handleFavorite}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200"
            >
              <i className="ri-add-line" aria-hidden="true"></i>
              加入片单
            </button>
          </div>
        </div>
      </div>

      {item.episodes.length > 0 && (
        <div className="mt-10">
          <EpisodeList episodes={item.episodes} onPlay={handlePlay} />
        </div>
      )}

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-6 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-2 w-2 rounded-full bg-accent-500"></span>
            相关推荐
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
            {related.map((media) => (
              <MediaCard key={media.id} item={media} />
            ))}
          </div>
        </section>
      )}

      <div className="mt-12 rounded-lg border border-background-200 bg-background-50 p-5 text-sm text-foreground-600">
        <p className="flex items-start gap-2">
          <i className="ri-information-line mt-0.5 text-primary-600" aria-hidden="true"></i>
          <span>
            本站为影视内容演示站点，页面中的播放、推荐等功能均为前端演示，不含真实片源与第三方接口。
            如需返回列表继续浏览，可前往
            <Link to={categoryTo} className="mx-1 font-medium text-primary-600 hover:text-accent-600">
              {categoryLabel}分类
            </Link>
            查看更多作品。
          </span>
        </p>
      </div>

      {notice && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 shadow-lg">
          {notice}
        </div>
      )}
    </div>
  );
}